import { type FetchClient, type PageParameters } from './http';
import type { VisibilityLevel } from './types/common';
import type { Model, ModelPage, ModelRequest, UpdateModelRequest } from './types/models';

export const deleteModel = ({
  client,
  workspaceId,
  modelId,
}: {
  client: FetchClient;
  workspaceId: string;
  modelId: string;
}): Promise<boolean> => {
  return client.del(`${client.baseUrl}/workspaces/${workspaceId}/models/${modelId}`);
};

export const getModel = ({
  client,
  workspaceId,
  modelId,
}: {
  client: FetchClient;
  workspaceId: string;
  modelId: string;
}): Promise<Model> => {
  return client.get(`${client.baseUrl}/workspaces/${workspaceId}/models/${modelId}`);
};

export const getModelByVersion = ({
  client,
  workspaceId,
  modelId,
  modelVersionId = 'last',
}: {
  client: FetchClient;
  workspaceId: string;
  modelId: string;
  modelVersionId?: string;
}): Promise<Model> => {
  return client.get(`${client.baseUrl}/workspaces/${workspaceId}/models/${modelId}/versions/${modelVersionId}`);
};

export const getModels = ({
  client,
  workspaceId,
  query = '',
  page = '1',
  size = '20',
  withCapabilities = false,
  sort = 'name',
}: {
  client: FetchClient;
  workspaceId: string;
  query?: string;
  withCapabilities?: boolean;
  sort?: string;
} & PageParameters): Promise<ModelPage> => {
  const capabilitiesParam = withCapabilities ? '&withCapabilities=true' : '';
  return client.get(
    `${client.baseUrl}/workspaces/${workspaceId}/models?q=${query}&page=${page}&size=${size}&sort=${sort}${capabilitiesParam}`
  );
};

export const createModel = ({
  client,
  workspaceId,
  modelRequest,
}: {
  client: FetchClient;
  workspaceId: string;
  modelRequest: ModelRequest;
}): Promise<Model> => {
  return client.post(`${client.baseUrl}/workspaces/${workspaceId}/models`, modelRequest);
};

export const updateOrVersionedModel = ({
  client,
  workspaceId,
  modelId,
  updateModelRequest,
}: {
  client: FetchClient;
  workspaceId: string;
  modelId: string;
  updateModelRequest: UpdateModelRequest;
}): Promise<Model> => {
  return client.patch(`${client.baseUrl}/workspaces/${workspaceId}/models/${modelId}`, updateModelRequest);
};

export const searchModels = ({
  client,
  query = '',
  page = '1',
  size = '20',
  sort = 'name',
  involved,
  owned,
  visibilityLevels,
  withCapabilities,
}: {
  client: FetchClient;
  query?: string;
  involved?: boolean;
  owned?: boolean;
  visibilityLevels?: VisibilityLevel[];
  withCapabilities?: boolean;
  sort?: string;
} & PageParameters): Promise<ModelPage> => {
  const url = new URL(`${client.baseUrl}/models`);
  url.searchParams.set('page', page);
  url.searchParams.set('size', size);
  url.searchParams.set('sort', sort);
  if (query) {
    url.searchParams.set('q', query);
  }
  if (involved !== undefined) {
    url.searchParams.set('involved', involved.toString());
  }
  if (owned !== undefined) {
    url.searchParams.set('owned', owned.toString());
  }
  if (withCapabilities !== undefined) {
    url.searchParams.set('withCapabilities', withCapabilities.toString());
  }
  if (visibilityLevels && visibilityLevels.length > 0) {
    visibilityLevels.forEach((level) => url.searchParams.append('visibilityLevels', level));
  }
  return client.get(url.toString());
};
